import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Moment from "moment";
import { useHistory, useLocation } from "react-router-dom";

import { SalesContext } from "../../context/salesContext";
import { CustomersContext } from "../../context/customersContext";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { SALE_STATUSES, STATUS_TONE } from "../../constants/saleStatus";
import {
    DEFAULT_SALES_PAGE_SIZE,
    resolveApiLimit,
    SALES_PAGE_SIZE_OPTIONS,
} from "../../constants/config";
import { Badge, DataTable, PageHeader, SearchBar } from "../ui";
import WorkOrderModal from "../modals/WorkOrderModal";
import WorkOrderStats from "./WorkOrderStats";

const FILTERS = [
    { key: "All", label: "All", tone: "brand", excludeStatuses: ["Completed"] },
    { key: "Processed", label: "Processed", tone: "blue", status: ["Processed"] },
    { key: "Parts", label: "Parts", tone: "orange", status: ["Pending Parts"] },
    { key: "Prep", label: "Prep", tone: "yellow", status: ["Prep"] },
    { key: "Ship", label: "Ship", tone: "gray", status: ["Ship"] },
    { key: "Paint", label: "Paint", tone: "darkBlue", status: ["Pending Paint"] },
    { key: "Door", label: "Door", tone: "neutral", status: ["Making Door"] },
    {
        key: "Ready",
        label: "Ready",
        tone: "green",
        status: ["Ready For Installation", "Ready For Pick Up"],
    },
    { key: "Completed", label: "Completed", tone: "darkGreen", status: ["Completed"] },
];

const renderPendingDays = (sale) => {
    if (!sale.expectedCompletionDate) return null;
    const expected = Moment(sale.expectedCompletionDate).startOf("day");
    const today = Moment().startOf("day");
    const pending = expected.diff(today, "days");

    if (pending < 0) {
        return (
            <span className="text-xs text-danger-600">
                {Math.abs(pending)} day{Math.abs(pending) === 1 ? "" : "s"} overdue
            </span>
        );
    }
    if (pending === 0) {
        return <span className="text-xs text-info-600">Due today</span>;
    }
    if (pending < 4) {
        return <span className="text-xs text-warning-700">Coming up soon</span>;
    }
    return (
        <span className="text-xs text-ink-500">
            {pending} day{pending === 1 ? "" : "s"} remaining
        </span>
    );
};

const EMPTY_MESSAGES = {
    All: "No active work orders.",
    Ready: "No jobs are ready for pick up or install.",
    Door: "No active door builds in the queue.",
    Parts: "Not waiting on any parts.",
    Processed: "Nothing newly processed.",
    Completed: "No work orders have been completed yet.",
};

const WorkOrders = () => {
    const { loading, salesList, salesPagination, action } = useContext(SalesContext);
    const { customers } = useContext(CustomersContext);

    const location = useLocation();
    const history = useHistory();

    const [filterKey, setFilterKey] = useState(
        () => new URLSearchParams(location.search).get("filter") || "All",
    );
    const [search, setSearch] = useState(
        () => new URLSearchParams(location.search).get("q") || "",
    );
    const [page, setPage] = useState(() => {
        const p = new URLSearchParams(location.search).get("page");
        return p !== null ? Math.max(0, parseInt(p, 10)) : 0;
    });
    const [rowsPerPage, setRowsPerPage] = useState(() => {
        const s = new URLSearchParams(location.search).get("size");
        return s !== null ? parseInt(s, 10) : DEFAULT_SALES_PAGE_SIZE;
    });
    const [sortBy, setSortBy] = useState(
        () => new URLSearchParams(location.search).get("sortBy") || "expectedCompletionDate",
    );
    const [sortOrder, setSortOrder] = useState(
        () => new URLSearchParams(location.search).get("sortOrder") || "asc",
    );
    const debouncedSearch = useDebouncedValue(search);

    useEffect(() => {
        const params = new URLSearchParams();
        if (page !== 0) params.set("page", page);
        if (rowsPerPage !== DEFAULT_SALES_PAGE_SIZE) params.set("size", rowsPerPage);
        if (sortBy !== "expectedCompletionDate") params.set("sortBy", sortBy);
        if (sortOrder !== "asc") params.set("sortOrder", sortOrder);
        if (filterKey !== "All") params.set("filter", filterKey);
        if (search) params.set("q", search);
        history.replace({ search: params.toString() });
    }, [page, rowsPerPage, sortBy, sortOrder, filterKey, search, history]);

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const activeFilter = useMemo(
        () => FILTERS.find((f) => f.key === filterKey) || FILTERS[0],
        [filterKey],
    );

    const refreshSalesList = useCallback(
        () =>
            action.getSalesList({
                page: page + 1,
                limit: resolveApiLimit(rowsPerPage),
                status: activeFilter.status,
                excludeStatuses: activeFilter.excludeStatuses,
                search: debouncedSearch || undefined,
                sortBy,
                sortOrder,
            }),
        [action, page, rowsPerPage, activeFilter, debouncedSearch, sortBy, sortOrder],
    );

    useEffect(() => {
        refreshSalesList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, filterKey, debouncedSearch, sortBy, sortOrder]);

    const handleSortChange = useCallback((field, direction) => {
        setSortBy(field);
        setSortOrder(direction);
        setPage(0);
    }, []);

    const handleFilterClick = (key) => {
        setFilterKey(key);
        setPage(0);
    };

    const customerById = useMemo(() => {
        const map = new Map();
        (customers || []).forEach((c) => map.set(c._id, c));
        return map;
    }, [customers]);

    const totalForFilter = (salesPagination && salesPagination.totalSales) || 0;

    const filterConfig = useMemo(
        () => ({
            id: "workOrderStatus",
            value: filterKey,
            onChange: handleFilterClick,
            options: FILTERS.map((f) => ({
                value: f.key,
                label: f.label,
                tone: f.tone,
            })),
        }),
        [filterKey],
    );

    const columns = useMemo(
        () => [
            {
                id: "orderDate",
                label: "Order Date",
                sortable: true,
                serverSortField: "orderDate",
                cell: (row) =>
                    row.orderDate ? Moment(row.orderDate).utc().format("MMM D, YYYY") : null,
            },
            {
                id: "customerId",
                label: "Customer",
                sortable: true,
                serverSortField: "customerId",
                sortValue: (row) => {
                    const c = customerById.get(row.customerId);
                    return c ? `${c.firstName || ""} ${c.lastName || ""}` : "";
                },
                cell: (row) => {
                    const c = customerById.get(row.customerId) || {};
                    return `${c.firstName || ""} ${c.lastName || ""}`.trim() || "—";
                },
            },
            {
                id: "po",
                label: "PO",
                sortable: true,
                serverSortField: "po",
                cellClassName: "",
            },
            {
                id: "doorSystem",
                label: "Door System",
                sortable: true,
                serverSortField: "productSpecification.doorSystem",
                cell: (row) => {
                    const spec = row.productSpecification || {};
                    const door = spec.doorSystem || "—";
                    const extras = [
                        spec.sidelightLeft?.sidelight === "Yes" && "Left sidelight",
                        spec.sidelightRight?.sidelight === "Yes" && "Right sidelight",
                        spec.transom?.transomBox === "Yes" && "Transom",
                    ].filter(Boolean);
                    return extras.length > 0 ? `${door}, ${extras.join(", ")}` : door;
                },
            },
            {
                id: "expectedCompletionDate",
                label: "Expected",
                sortable: true,
                serverSortField: "expectedCompletionDate",
                cell: (row) =>
                    row.expectedCompletionDate
                        ? Moment(row.expectedCompletionDate).format("MMM D, YYYY")
                        : null,
            },
            {
                id: "pending",
                label: "Pending",
                sortable: true,
                serverSortField: "pendingDays",
                cell: renderPendingDays,
            },
            {
                id: "status",
                label: "Status",
                sortable: true,
                serverSortField: "workOrder.status",
                sortValue: (row) => (row.workOrder && row.workOrder.status) || "",
                cell: (row) => {
                    const status = (row.workOrder && row.workOrder.status) || "";
                    const tone = STATUS_TONE[status] || "neutral";
                    return status ? <Badge tone={tone}>{status}</Badge> : null;
                },
            },
        ],
        [customerById],
    );

    const bulkActions = useMemo(
        () => [
            {
                label: "Delete",
                variant: "danger",
                onClick: async (selectedIds) => {
                    await Promise.all(selectedIds.map((id) => action.deleteSale(id)));
                },
                successMessage: "Record(s) successfully deleted",
                errorMessage: "An error occurred while deleting. Please contact support.",
            },
        ],
        [action],
    );

    const bulkEdit = useMemo(
        () => ({
            title: "Bulk edit work orders",
            description: "Apply the same changes to every selected work order.",
            successMessage: "Work order(s) successfully updated",
            fields: [
                {
                    id: "status",
                    label: "Status",
                    type: "select",
                    placeholder: "Select a status",
                    options: SALE_STATUSES.map((status) => ({
                        value: status,
                        label: status,
                    })),
                },
            ],
            onSubmit: async (selectedIds, values) => {
                const rowsById = new Map((salesList || []).map((sale) => [sale._id, sale]));
                await Promise.all(
                    selectedIds.map((id) => {
                        const sale = rowsById.get(id);
                        if (!sale) return Promise.resolve();
                        const updated = { ...sale };
                        if (values.status !== undefined) {
                            updated.workOrder = {
                                ...(sale.workOrder || {}),
                                status: values.status,
                            };
                        }
                        return action.updateSale(id, updated);
                    }),
                );
                await refreshSalesList();
            },
        }),
        [action, salesList, refreshSalesList],
    );

    return (
        <div className="space-y-6">
            <PageHeader title="Work Orders" description="Track every active job by status." />

            <WorkOrderStats />

            <WorkOrderModal
                open={modalOpen}
                id={selectedId}
                onClose={() => setModalOpen(false)}
            />

            <DataTable
                rows={salesList || []}
                columns={columns}
                onSortChange={handleSortChange}
                defaultSort={{
                    id: { "productSpecification.doorSystem": "doorSystem", "pendingDays": "pending", "workOrder.status": "status" }[sortBy] || sortBy,
                    direction: sortOrder,
                }}
                topContent={
                    <SearchBar
                        id="workorders-search"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        placeholder="Search PO #, customer, or phone number"
                    />
                }
                selectable
                bulkActions={bulkActions}
                bulkEdit={bulkEdit}
                filters={[filterConfig]}
                loading={loading}
                emptyMessage={EMPTY_MESSAGES[filterKey] || EMPTY_MESSAGES.All}
                onRowClick={(row) => {
                    setSelectedId(row._id);
                    setModalOpen(true);
                }}
                totalCount={totalForFilter}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={SALES_PAGE_SIZE_OPTIONS}
                onPageChange={setPage}
                onRowsPerPageChange={(rows) => {
                    setRowsPerPage(rows);
                    setPage(0);
                }}
            />
        </div>
    );
};

export default WorkOrders;
